import {
  LeadContactChannel,
  type Lead,
  type LeadStatus,
  LeadsApi,
  Permission,
} from '@up2stay/up2promo-api-client';
import { formatLeadDisplayName } from '@up2stay/up2promo-commons';
import {
  Button,
  cn,
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  Input,
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@up2stay/up2promo-ui';
import { useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArchiveRestore,
  Check,
  ChevronRight,
  CircleDot,
  Contact,
  Copy,
  Download,
  Mail,
  MessageSquare,
  Phone,
  SquareArrowOutUpRight,
  Tag,
  Trash2,
  UserCheck,
} from 'lucide-react';
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';

import { invalidateStatusBoardQueries } from '@/core/components/dashboard/dashboard-refresh-keys';
import {
  LeadAssigneePicker,
  type LeadAssigneePickerLabels,
} from '@/core/components/leads/lead-assignee-picker';
import {
  LEAD_STATUS_IDS,
  leadStatusAccent,
} from '@/core/components/leads/lead-status-colors';
import { LeadDetailDeleteDialog } from '@/core/components/leads/lead-detail/lead-detail-delete-dialog';
import { shellAwarePopoverCollision } from '@/core/hooks/use-css-keyboard-inset';
import { useLongPressMenu } from '@/core/hooks/use-long-press-menu';
import { usePermissions } from '@/core/hooks/use-permissions';
import {
  canMutateLeadRecord,
  isLeadArchived,
} from '@/core/lib/lead-mutate-access';
import {
  triggerDragStartHaptic,
  triggerErrorHaptic,
  triggerSelectionHaptic,
  triggerSuccessHaptic,
  triggerTapHaptic,
} from '@/core/lib/dnd-haptics';
import { leadHasVcfContent } from '@/core/lib/leads-vcf';
import { openInSystemBrowser } from '@/core/lib/open-external-url';
import { saveLeadToDevice, shareLeadVcf } from '@/core/lib/save-lead-to-device';
import { leadsQueryKeys } from '@/core/query-keys/leads-query-keys';
import { useAuthStore } from '@/core/store/auth.store';
import { isCapacitorNative } from '@/core/utils/capacitor.utils';

export type LeadsListContextMenuLabels = {
  menuLabel: string;
  bulkTitle: string;
  open: string;
  status: string;
  assignee: string;
  assign: string;
  tags: string;
  archive: string;
  unarchive: string;
  delete: string;
  deleteN: string;
  saveToContacts: string;
  downloadVCard: string;
  exportCsv: string;
  call: string;
  email: string;
  sms: string;
  copy: string;
  tagPlaceholder: string;
  applyTag: string;
  existingTags: string;
  noExistingTags: string;
  cancel: string;
  deleteConfirmTitle: string;
  deleteConfirmDescription: string;
  deleteConfirm: string;
  saving: string;
  updateError: string;
  deleteError: string;
  archiveError: string;
  unarchiveError: string;
  copyError: string;
  saveEmptyError: string;
  saveFailedError: string;
};

/**
 * K5 fixed shortcuts — locale-independent Latin glyphs/codes.
 * Never derive these from translated menu labels.
 */
const LEADS_CTX_SHORTCUT = {
  open: '↵',
  status: 'S',
  assignee: 'A',
  tags: 'T',
  archive: 'E',
  delete: '⌫',
} as const;

type DesktopSubMenu = 'status' | 'assignee' | 'tags';

export type LeadsListContextMenuProps = {
  lead: Lead;
  children: (state: { highlighted: boolean }) => ReactNode;
  variant: 'desktop' | 'mobile';
  workspaceId: string;
  locale: string;
  selectedLeadIds: ReadonlySet<string>;
  /** Selected leads currently visible in the filtered list (for bulk actions). */
  selectedLeads: Lead[];
  statusLabels: Record<string, string>;
  assigneeLabel?: string;
  assigneePickerLabels: LeadAssigneePickerLabels;
  existingTags: string[];
  labels: LeadsListContextMenuLabels;
  canExport: boolean;
  workspaceLeadsAlwaysShared?: boolean;
  onOpenLead: (lead: Lead) => void;
  onExportLeads: (leads: Lead[]) => void;
  onRefresh: () => void;
};

type MobileSheet = 'status' | 'assignee' | null;

/** K5 desktop item density — ListActions.dc */
const desktopItemClass =
  'min-h-8 gap-[9px] rounded-[7px] px-[9px] py-0 text-[13px] font-medium [&_svg]:size-3.5';

const desktopShortcutClass =
  'ml-auto font-mono text-[11px] tracking-normal text-muted-foreground';

const desktopActionTileClass =
  'flex flex-col items-center justify-center gap-[3px] rounded-lg bg-[color:var(--surface-2)] py-[7px] text-[11.5px] font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-40';

/** K1 mobile floating card rows — ListActions.dc */
const mobileRowClass =
  'flex min-h-12 w-full items-center gap-3 px-4 text-left text-[15px] font-medium transition-colors hover:bg-accent/60 disabled:opacity-50';

const mobileActionTileClass =
  'flex min-h-14 flex-col items-center justify-center gap-1 rounded-xl bg-[color:var(--surface-2)] text-[12px] font-semibold text-foreground transition-colors hover:bg-muted disabled:opacity-40';

function compactPhone(value: string): string {
  return value.replace(/[^\d+]/g, '');
}

function copyPayloadForLead(lead: Lead): string {
  const name = formatLeadDisplayName(lead.firstName, lead.lastName);
  const parts = [name, lead.email?.trim(), lead.phone?.trim()].filter(
    (part): part is string => Boolean(part),
  );
  return parts.join('\n');
}

function canMutateLead(
  lead: Lead,
  viewerUserId: string | undefined,
  canUpdate: boolean,
  canUpdateOthers: boolean,
  workspaceLeadsAlwaysShared?: boolean,
): boolean {
  return canMutateLeadRecord({
    lead,
    viewerUserId,
    canUpdate,
    canUpdateOthers,
    workspaceLeadsAlwaysShared,
  });
}

export function LeadsListRowContextMenu({
  lead,
  children,
  variant,
  workspaceId,
  locale,
  selectedLeadIds,
  selectedLeads,
  statusLabels,
  assigneeLabel,
  assigneePickerLabels,
  existingTags,
  labels,
  canExport,
  workspaceLeadsAlwaysShared,
  onOpenLead,
  onExportLeads,
  onRefresh,
}: LeadsListContextMenuProps) {
  const { can } = usePermissions();
  const user = useAuthStore((state) => state.user);
  const queryClient = useQueryClient();
  const canUpdate = can(Permission.LEADS_UPDATE);
  const canUpdateOthers = can(Permission.LEADS_UPDATE_OTHERS);

  const [menuMode, setMenuMode] = useState<'single' | 'bulk'>('single');
  const [desktopMenuOpen, setDesktopMenuOpen] = useState(false);
  const [desktopSub, setDesktopSub] = useState<DesktopSubMenu | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSheet, setMobileSheet] = useState<MobileSheet>(null);
  const [collisionPadding, setCollisionPadding] = useState(
    shellAwarePopoverCollision,
  );
  const [busy, setBusy] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
  const [tagDraft, setTagDraft] = useState('');

  const closeMobileMenu = useCallback(() => {
    setMobileOpen(false);
  }, []);

  const openMobileSheet = useCallback(
    (sheet: Exclude<MobileSheet, null>) => {
      closeMobileMenu();
      // Open the shared bottom sheet after the floating menu unmounts so
      // Radix popover + Drawer overlays do not nest.
      queueMicrotask(() => setMobileSheet(sheet));
    },
    [closeMobileMenu],
  );

  const closeMobileSheet = useCallback(() => {
    setMobileSheet(null);
  }, []);

  const phone = lead.phone?.trim() ?? '';
  const email = lead.email?.trim() ?? '';
  const phoneHref = phone ? compactPhone(phone) : '';
  const canSave = leadHasVcfContent(lead);
  const leadName =
    formatLeadDisplayName(lead.firstName, lead.lastName) ||
    email ||
    phone ||
    '—';
  const statusLabel = statusLabels[lead.status] ?? lead.status;
  const assigneeDisplay = assigneeLabel?.trim() || assigneePickerLabels.nobody;

  const targetLeads = useMemo(() => {
    if (menuMode === 'bulk') return selectedLeads;
    return [lead];
  }, [lead, menuMode, selectedLeads]);

  const targetIds = useMemo(
    () => targetLeads.map((item) => item.id),
    [targetLeads],
  );

  const mutableTargets = useMemo(() => {
    return targetLeads.filter((item) =>
      canMutateLead(
        item,
        user?.id,
        canUpdate,
        canUpdateOthers,
        workspaceLeadsAlwaysShared,
      ),
    );
  }, [
    targetLeads,
    user?.id,
    canUpdate,
    canUpdateOthers,
    workspaceLeadsAlwaysShared,
  ]);

  const mutableTargetIds = useMemo(
    () => mutableTargets.map((item) => item.id),
    [mutableTargets],
  );

  const archiveTargetIds = useMemo(
    () =>
      mutableTargets
        .filter((item) => !isLeadArchived(item))
        .map((item) => item.id),
    [mutableTargets],
  );

  const unarchiveTargetIds = useMemo(
    () =>
      mutableTargets
        .filter((item) => isLeadArchived(item))
        .map((item) => item.id),
    [mutableTargets],
  );

  const canMutateTargets = mutableTargetIds.length > 0;
  const canMutateThis = canMutateLead(
    lead,
    user?.id,
    canUpdate,
    canUpdateOthers,
    workspaceLeadsAlwaysShared,
  );
  const thisLeadArchived = isLeadArchived(lead);
  const showBulkArchive = archiveTargetIds.length > 0;
  const showBulkUnarchive = unarchiveTargetIds.length > 0;

  const refreshLists = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: leadsQueryKeys.root }),
      invalidateStatusBoardQueries(queryClient),
    ]);
    onRefresh();
  }, [onRefresh, queryClient]);

  const logContactVia = useCallback(
    async (channel: LeadContactChannel, target: Lead) => {
      try {
        await LeadsApi.contactVia({
          path: { workspaceId, id: target.id },
          body: { channel },
        });
      } catch {
        // Best-effort activity log; OS handoff already happened.
      }
    },
    [workspaceId],
  );

  const handleCall = useCallback(() => {
    if (!phoneHref) return;
    void openInSystemBrowser(`tel:${phoneHref}`);
    void logContactVia(LeadContactChannel.CALL, lead);
  }, [lead, logContactVia, phoneHref]);

  const handleEmail = useCallback(() => {
    if (!email) return;
    void openInSystemBrowser(`mailto:${email}`);
    void logContactVia(LeadContactChannel.EMAIL, lead);
  }, [email, lead, logContactVia]);

  const handleSms = useCallback(() => {
    if (!phoneHref) return;
    void openInSystemBrowser(`sms:${phoneHref}`);
    void logContactVia(LeadContactChannel.SMS, lead);
  }, [lead, logContactVia, phoneHref]);

  const handleCopy = useCallback(async () => {
    const payload = copyPayloadForLead(lead);
    if (!payload) return;
    try {
      await navigator.clipboard.writeText(payload);
      void triggerTapHaptic();
    } catch {
      toast.error(labels.copyError);
    }
  }, [labels.copyError, lead]);

  const handleSetStatus = useCallback(
    async (status: LeadStatus) => {
      if (mutableTargetIds.length === 0) return;
      setBusy(true);
      try {
        if (mutableTargetIds.length === 1) {
          await LeadsApi.update({
            path: { workspaceId, id: mutableTargetIds[0]! },
            body: { status },
          });
        } else {
          await LeadsApi.bulkUpdate({
            path: { workspaceId },
            body: { leadIds: mutableTargetIds, setStatus: status },
          });
        }
        void triggerSuccessHaptic();
        await refreshLists();
      } catch (error) {
        console.error('Lead status update failed', error);
        void triggerErrorHaptic();
        toast.error(labels.updateError);
      } finally {
        setBusy(false);
        setMobileOpen(false);
        setMobileSheet(null);
      }
    },
    [labels.updateError, mutableTargetIds, refreshLists, workspaceId],
  );

  const handleSetAssignee = useCallback(
    async (userId: string | null) => {
      if (mutableTargetIds.length === 0) return;
      setBusy(true);
      try {
        if (mutableTargetIds.length === 1) {
          await LeadsApi.update({
            path: { workspaceId, id: mutableTargetIds[0]! },
            body: { assigneeUserId: userId },
          });
        } else {
          await LeadsApi.bulkUpdate({
            path: { workspaceId },
            body: {
              leadIds: mutableTargetIds,
              setAssigneeUserId: userId,
            },
          });
        }
        void triggerSuccessHaptic();
        await refreshLists();
      } catch (error) {
        console.error('Lead assignee update failed', error);
        void triggerErrorHaptic();
        toast.error(labels.updateError);
      } finally {
        setBusy(false);
        setMobileOpen(false);
        setMobileSheet(null);
      }
    },
    [labels.updateError, mutableTargetIds, refreshLists, workspaceId],
  );

  const handleAddTag = useCallback(
    async (tag: string) => {
      const next = tag.trim();
      if (!next || mutableTargetIds.length === 0) return;
      setBusy(true);
      try {
        if (mutableTargetIds.length === 1) {
          const current = lead.tags ?? [];
          if (current.includes(next)) {
            setBusy(false);
            return;
          }
          await LeadsApi.update({
            path: { workspaceId, id: mutableTargetIds[0]! },
            body: { tags: [...current, next] },
          });
        } else {
          await LeadsApi.bulkUpdate({
            path: { workspaceId },
            body: { leadIds: mutableTargetIds, addTags: [next] },
          });
        }
        void triggerSuccessHaptic();
        setTagDraft('');
        await refreshLists();
      } catch (error) {
        console.error('Lead tag update failed', error);
        void triggerErrorHaptic();
        toast.error(labels.updateError);
      } finally {
        setBusy(false);
        setMobileOpen(false);
      }
    },
    [
      labels.updateError,
      lead.tags,
      mutableTargetIds,
      refreshLists,
      workspaceId,
    ],
  );

  const handleSetArchived = useCallback(
    async (archived: boolean) => {
      const ids = archived ? archiveTargetIds : unarchiveTargetIds;
      if (ids.length === 0) return;
      setBusy(true);
      try {
        await Promise.all(
          ids.map((id) =>
            LeadsApi.update({
              path: { workspaceId, id },
              body: { archived },
            }),
          ),
        );
        void triggerSuccessHaptic();
        await refreshLists();
      } catch (error) {
        console.error(
          archived ? 'Lead archive failed' : 'Lead unarchive failed',
          error,
        );
        void triggerErrorHaptic();
        toast.error(archived ? labels.archiveError : labels.unarchiveError);
      } finally {
        setBusy(false);
        setMobileOpen(false);
      }
    },
    [
      archiveTargetIds,
      labels.archiveError,
      labels.unarchiveError,
      refreshLists,
      unarchiveTargetIds,
      workspaceId,
    ],
  );

  const requestDelete = useCallback(() => {
    if (mutableTargetIds.length === 0) return;
    setPendingDeleteIds(mutableTargetIds);
    setDeleteOpen(true);
    setMobileOpen(false);
  }, [mutableTargetIds]);

  const confirmDelete = useCallback(async () => {
    if (pendingDeleteIds.length === 0) return;
    setBusy(true);
    try {
      await Promise.all(
        pendingDeleteIds.map((id) =>
          LeadsApi.remove({ path: { workspaceId, id } }),
        ),
      );
      void triggerSuccessHaptic();
      setDeleteOpen(false);
      setPendingDeleteIds([]);
      await refreshLists();
    } catch (error) {
      console.error('Lead delete failed', error);
      void triggerErrorHaptic();
      toast.error(labels.deleteError);
    } finally {
      setBusy(false);
    }
  }, [labels.deleteError, pendingDeleteIds, refreshLists, workspaceId]);

  const handleSaveToContacts = useCallback(async () => {
    if (busy || !canSave) {
      if (!canSave) toast.error(labels.saveEmptyError);
      return;
    }
    void triggerTapHaptic();
    setBusy(true);
    try {
      const result = await saveLeadToDevice(lead);
      if (result.outcome === 'empty') {
        void triggerErrorHaptic();
        toast.error(labels.saveEmptyError);
        return;
      }
      if (result.outcome === 'contacts') {
        void triggerSuccessHaptic();
      }
      setMobileOpen(false);
    } catch (error) {
      console.error('Save lead to device failed', error);
      void triggerErrorHaptic();
      toast.error(labels.saveFailedError);
    } finally {
      setBusy(false);
    }
  }, [busy, canSave, labels.saveEmptyError, labels.saveFailedError, lead]);

  const handleDownloadVCard = useCallback(async () => {
    if (busy || !canSave) {
      if (!canSave) toast.error(labels.saveEmptyError);
      return;
    }
    void triggerTapHaptic();
    setBusy(true);
    try {
      const result = await shareLeadVcf(lead);
      if (result.outcome === 'empty') {
        void triggerErrorHaptic();
        toast.error(labels.saveEmptyError);
        return;
      }
      setMobileOpen(false);
    } catch (error) {
      console.error('vCard download failed', error);
      void triggerErrorHaptic();
      toast.error(labels.saveFailedError);
    } finally {
      setBusy(false);
    }
  }, [busy, canSave, labels.saveEmptyError, labels.saveFailedError, lead]);

  const openBulkExport = useCallback(() => {
    onExportLeads(targetLeads);
  }, [onExportLeads, targetLeads]);

  const resolveMenuMode = useCallback((): 'single' | 'bulk' => {
    return selectedLeadIds.size >= 2 && selectedLeadIds.has(lead.id)
      ? 'bulk'
      : 'single';
  }, [lead.id, selectedLeadIds]);

  const longPress = useLongPressMenu(() => {
    void triggerDragStartHaptic();
    setMenuMode('single');
    setCollisionPadding(shellAwarePopoverCollision());
    setMobileOpen(true);
  });

  const highlighted =
    (variant === 'mobile' && (mobileOpen || longPress.isHolding)) || false;

  const filteredExistingTags = useMemo(() => {
    const q = tagDraft.trim().toLowerCase();
    if (!q) return existingTags.slice(0, 12);
    return existingTags
      .filter((tag) => tag.toLowerCase().includes(q))
      .slice(0, 12);
  }, [existingTags, tagDraft]);

  const statusItems = (
    onPick: (status: LeadStatus) => void,
    withShortcuts: boolean,
  ) =>
    LEAD_STATUS_IDS.map((status, index) => {
      const current = menuMode === 'single' && lead.status === status;
      return (
        <button
          key={status}
          type="button"
          role="menuitemradio"
          aria-checked={current}
          disabled={busy || !canMutateTargets}
          className={cn(
            desktopItemClass,
            'flex w-full items-center text-left transition-colors hover:bg-[color:var(--surface-2)] disabled:opacity-50',
            current && 'bg-[color:var(--surface-2)] font-semibold',
          )}
          onClick={() => {
            if (current) return;
            onPick(status);
          }}
        >
          <span
            className="size-[7px] shrink-0 rounded-full"
            style={{ backgroundColor: leadStatusAccent(status) }}
            aria-hidden
          />
          <span className="min-w-0 flex-1 truncate">
            {statusLabels[status] ?? status}
          </span>
          {current ? (
            <Check className="size-3.5 shrink-0 text-primary" aria-hidden />
          ) : withShortcuts ? (
            <span className={desktopShortcutClass}>{index + 1}</span>
          ) : null}
        </button>
      );
    });

  const assigneePanel = (
    <LeadAssigneePicker
      workspaceId={workspaceId}
      ownerUserId={lead.userId?.trim() ?? ''}
      ownerOptionLabel=""
      assigneeUserId={lead.assigneeUserId}
      triggerLabel=""
      placeholder=""
      labels={assigneePickerLabels}
      locale={locale}
      presentation="inline"
      confirmLayout="popover"
      selectedCount={targetIds.length}
      hideNobody={false}
      showSavedCue={false}
      workspaceLeadsAlwaysShared={workspaceLeadsAlwaysShared}
      onAssigneeChange={(userId) => {
        void handleSetAssignee(userId);
      }}
    />
  );

  const tagsPanel = (
    <div className="space-y-2 p-1">
      <Input
        value={tagDraft}
        onChange={(e) => setTagDraft(e.target.value)}
        placeholder={labels.tagPlaceholder}
        disabled={busy || !canMutateTargets}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void handleAddTag(tagDraft);
          }
        }}
      />
      <Button
        type="button"
        size="sm"
        className="w-full"
        disabled={busy || !canMutateTargets || !tagDraft.trim()}
        onClick={() => void handleAddTag(tagDraft)}
      >
        {labels.applyTag}
      </Button>
      <div className="space-y-1">
        <p className="px-0.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {labels.existingTags}
        </p>
        {filteredExistingTags.length === 0 ? (
          <p className="px-0.5 text-[12.5px] text-muted-foreground">
            {labels.noExistingTags}
          </p>
        ) : (
          filteredExistingTags.map((tag) => (
            <button
              key={tag}
              type="button"
              className="flex h-8 w-full items-center rounded-md px-2 text-left text-[13px] hover:bg-accent"
              disabled={busy || !canMutateTargets}
              onClick={() => void handleAddTag(tag)}
            >
              {tag}
            </button>
          ))
        )}
      </div>
    </div>
  );

  const openDesktopSub = useCallback((sub: DesktopSubMenu | null) => {
    setDesktopSub(sub);
  }, []);

  const handleDesktopKeyDown = useCallback(
    (event: KeyboardEvent | ReactKeyboardEvent) => {
      if (busy) return;
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        target.closest('input, textarea, [contenteditable="true"]')
      ) {
        return;
      }

      const key = event.key;
      const lower = key.toLowerCase();
      const mutateOk = menuMode === 'bulk' ? canMutateTargets : canMutateThis;

      if (menuMode === 'single' && key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        onOpenLead(lead);
        return;
      }

      if ((key === 'Backspace' || key === 'Delete') && mutateOk) {
        event.preventDefault();
        event.stopPropagation();
        requestDelete();
        return;
      }

      if (lower === 'e' && mutateOk) {
        event.preventDefault();
        event.stopPropagation();
        if (menuMode === 'bulk') {
          // Prefer archive when any active leads are selected; else restore.
          void handleSetArchived(showBulkArchive);
        } else {
          void handleSetArchived(!thisLeadArchived);
        }
        return;
      }

      if (lower === 's' && mutateOk) {
        event.preventDefault();
        event.stopPropagation();
        openDesktopSub('status');
        return;
      }

      if (lower === 'a' && mutateOk) {
        event.preventDefault();
        event.stopPropagation();
        openDesktopSub('assignee');
        return;
      }

      if (lower === 't' && mutateOk) {
        event.preventDefault();
        event.stopPropagation();
        openDesktopSub('tags');
        return;
      }

      const statusIndex = Number.parseInt(key, 10);
      if (
        statusIndex >= 1 &&
        statusIndex <= LEAD_STATUS_IDS.length &&
        mutateOk
      ) {
        event.preventDefault();
        event.stopPropagation();
        const status = LEAD_STATUS_IDS[statusIndex - 1];
        if (status) void handleSetStatus(status);
      }
    },
    [
      busy,
      canMutateTargets,
      canMutateThis,
      handleSetArchived,
      handleSetStatus,
      lead,
      menuMode,
      onOpenLead,
      openDesktopSub,
      requestDelete,
      showBulkArchive,
      thisLeadArchived,
    ],
  );

  useEffect(() => {
    if (!desktopMenuOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      handleDesktopKeyDown(event);
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [desktopMenuOpen, handleDesktopKeyDown]);

  const deleteDialog = (
    <LeadDetailDeleteDialog
      open={deleteOpen}
      onOpenChange={setDeleteOpen}
      title={labels.deleteConfirmTitle}
      description={labels.deleteConfirmDescription}
      cancelLabel={labels.cancel}
      confirmLabel={
        pendingDeleteIds.length > 1
          ? labels.deleteN.replace('{count}', String(pendingDeleteIds.length))
          : labels.deleteConfirm
      }
      savingLabel={labels.saving}
      isDeleting={busy}
      onConfirm={() => {
        void confirmDelete();
      }}
    />
  );

  if (variant === 'mobile') {
    const mobileScrim =
      mobileOpen && typeof document !== 'undefined'
        ? createPortal(
            <button
              type="button"
              aria-label={labels.cancel}
              className="fixed inset-0 z-[10040] border-0 bg-black/32 backdrop-blur-[6px]"
              onClick={closeMobileMenu}
            />,
            document.body,
          )
        : null;

    return (
      <>
        <Popover open={mobileOpen} onOpenChange={setMobileOpen}>
          <PopoverAnchor asChild>
            <div
              className={cn(mobileOpen && 'relative z-[10045]')}
              onContextMenu={(event) => {
                event.preventDefault();
                longPress.onContextMenu(event);
              }}
              onPointerDown={longPress.onPointerDown}
              onPointerMove={longPress.onPointerMove}
              onPointerUp={longPress.onPointerUp}
              onPointerCancel={longPress.onPointerCancel}
              onClick={() => {
                if (longPress.consumeClickIfMenuOpened()) return;
                onOpenLead(lead);
              }}
            >
              {children({ highlighted })}
            </div>
          </PopoverAnchor>

          {mobileScrim}

          <PopoverContent
            role="menu"
            aria-label={labels.menuLabel}
            side="bottom"
            align="start"
            sideOffset={10}
            sticky="always"
            collisionPadding={collisionPadding}
            onOpenAutoFocus={(event) => event.preventDefault()}
            onCloseAutoFocus={(event) => event.preventDefault()}
            className="z-[10050] w-[min(17.5rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border-0 bg-card p-0 text-card-foreground shadow-[0_12px_40px_rgba(0,0,0,0.25)]"
          >
            <div className="overflow-hidden">
              <div className="grid grid-cols-3 gap-1.5 p-2">
                <button
                  type="button"
                  disabled={!phoneHref}
                  className={mobileActionTileClass}
                  onClick={() => {
                    closeMobileMenu();
                    handleCall();
                  }}
                >
                  <Phone className="size-[18px]" aria-hidden />
                  {labels.call}
                </button>
                <button
                  type="button"
                  disabled={!email}
                  className={mobileActionTileClass}
                  onClick={() => {
                    closeMobileMenu();
                    handleEmail();
                  }}
                >
                  <Mail className="size-[18px]" aria-hidden />
                  {labels.email}
                </button>
                <button
                  type="button"
                  disabled={!phoneHref}
                  className={mobileActionTileClass}
                  onClick={() => {
                    closeMobileMenu();
                    handleSms();
                  }}
                >
                  <MessageSquare className="size-[18px]" aria-hidden />
                  {labels.sms}
                </button>
              </div>

              {canMutateThis ? (
                <div className="border-t border-[color:var(--surface-border)]">
                  <button
                    type="button"
                    className={mobileRowClass}
                    onClick={() => openMobileSheet('status')}
                  >
                    <CircleDot
                      className="size-[17px] shrink-0 text-muted-foreground"
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1">{labels.status}</span>
                    <span className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
                      <span
                        className="size-[7px] rounded-full"
                        style={{
                          backgroundColor: leadStatusAccent(lead.status),
                        }}
                        aria-hidden
                      />
                      {statusLabel}
                    </span>
                    <ChevronRight
                      className="size-[15px] text-muted-foreground"
                      aria-hidden
                    />
                  </button>
                  <button
                    type="button"
                    className={cn(
                      mobileRowClass,
                      'border-t border-[color:var(--surface-border)]',
                    )}
                    onClick={() => openMobileSheet('assignee')}
                  >
                    <UserCheck
                      className="size-[17px] shrink-0 text-muted-foreground"
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1">{labels.assignee}</span>
                    <span className="text-[13px] text-muted-foreground">
                      {assigneeDisplay.split(/\s+/)[0] ?? assigneeDisplay}
                    </span>
                    <ChevronRight
                      className="size-[15px] text-muted-foreground"
                      aria-hidden
                    />
                  </button>
                </div>
              ) : null}

              <div className="border-t-[6px] border-[color:var(--surface-2)]">
                <button
                  type="button"
                  disabled={busy || !canSave}
                  className={mobileRowClass}
                  onClick={() => {
                    closeMobileMenu();
                    void handleSaveToContacts();
                  }}
                >
                  <Contact
                    className="size-[17px] shrink-0 text-muted-foreground"
                    aria-hidden
                  />
                  {labels.saveToContacts}
                </button>
                {canMutateThis ? (
                  <button
                    type="button"
                    disabled={busy}
                    className={cn(
                      mobileRowClass,
                      'border-t border-[color:var(--surface-border)]',
                    )}
                    onClick={() => {
                      closeMobileMenu();
                      void handleSetArchived(!thisLeadArchived);
                    }}
                  >
                    {thisLeadArchived ? (
                      <ArchiveRestore
                        className="size-[17px] shrink-0 text-muted-foreground"
                        aria-hidden
                      />
                    ) : (
                      <Archive
                        className="size-[17px] shrink-0 text-muted-foreground"
                        aria-hidden
                      />
                    )}
                    {thisLeadArchived ? labels.unarchive : labels.archive}
                  </button>
                ) : null}
              </div>

              {canMutateThis ? (
                <div className="border-t-[6px] border-[color:var(--surface-2)]">
                  <button
                    type="button"
                    disabled={busy}
                    className={cn(mobileRowClass, 'text-destructive')}
                    onClick={requestDelete}
                  >
                    <Trash2 className="size-[17px] shrink-0" aria-hidden />
                    {labels.delete}
                  </button>
                </div>
              ) : null}
            </div>
          </PopoverContent>
        </Popover>

        <Drawer
          open={mobileSheet === 'status'}
          onOpenChange={(open) => {
            if (!open) closeMobileSheet();
          }}
        >
          <DrawerContent
            fitContent
            scrollable={false}
            className="rounded-t-3xl"
          >
            <DrawerHeader className="flex flex-row items-center justify-between gap-3 px-4 pb-3 pt-1 text-left sm:text-left">
              <button
                type="button"
                className="min-w-[4.5rem] text-left text-[15px] text-muted-foreground"
                onClick={closeMobileSheet}
              >
                {labels.cancel}
              </button>
              <DrawerTitle className="text-center text-[16px] font-bold">
                {labels.status}
              </DrawerTitle>
              <span className="min-w-[4.5rem]" aria-hidden />
            </DrawerHeader>
            <DrawerDescription className="sr-only">
              {leadName}
            </DrawerDescription>
            <div className="flex flex-col gap-0.5 px-2 pb-6">
              {LEAD_STATUS_IDS.map((status) => {
                const selected = lead.status === status;
                return (
                  <button
                    key={status}
                    type="button"
                    disabled={busy || !canMutateTargets}
                    className={cn(
                      'flex min-h-[52px] items-center gap-3 rounded-xl px-3 text-[15px] disabled:opacity-50',
                      selected && 'bg-[color:var(--surface-2)] font-semibold',
                    )}
                    onClick={() => {
                      void triggerSelectionHaptic();
                      closeMobileSheet();
                      if (selected) return;
                      void handleSetStatus(status);
                    }}
                  >
                    <span
                      className="size-[7px] shrink-0 rounded-full"
                      style={{ backgroundColor: leadStatusAccent(status) }}
                      aria-hidden
                    />
                    <span className="flex-1 text-left">
                      {statusLabels[status] ?? status}
                    </span>
                    {selected ? (
                      <Check className="size-[18px] text-primary" aria-hidden />
                    ) : null}
                  </button>
                );
              })}
            </div>
          </DrawerContent>
        </Drawer>

        <Drawer
          open={mobileSheet === 'assignee'}
          onOpenChange={(open) => {
            if (!open) closeMobileSheet();
          }}
        >
          <DrawerContent fitContent className="rounded-t-3xl">
            <DrawerHeader className="flex flex-row items-center justify-between gap-3 px-4 pb-3 pt-1 text-left sm:text-left">
              <button
                type="button"
                className="min-w-[4.5rem] text-left text-[15px] text-muted-foreground"
                onClick={closeMobileSheet}
              >
                {labels.cancel}
              </button>
              <DrawerTitle className="text-center text-[16px] font-bold">
                {labels.assignee}
              </DrawerTitle>
              <span className="min-w-[4.5rem]" aria-hidden />
            </DrawerHeader>
            <DrawerDescription className="sr-only">
              {leadName}
            </DrawerDescription>
            <div className="px-1 pb-2">
              <LeadAssigneePicker
                workspaceId={workspaceId}
                ownerUserId={lead.userId?.trim() ?? ''}
                ownerOptionLabel=""
                assigneeUserId={lead.assigneeUserId}
                triggerLabel=""
                placeholder=""
                labels={assigneePickerLabels}
                locale={locale}
                presentation="inline"
                confirmLayout="sheet"
                selectedCount={targetIds.length}
                hideNobody={false}
                showSavedCue={false}
                workspaceLeadsAlwaysShared={workspaceLeadsAlwaysShared}
                onAssigneeChange={(userId) => {
                  closeMobileSheet();
                  void handleSetAssignee(userId);
                }}
              />
            </div>
          </DrawerContent>
        </Drawer>

        {deleteDialog}
      </>
    );
  }

  const bulkTitle = labels.bulkTitle.replace(
    '{count}',
    String(selectedLeadIds.size),
  );

  return (
    <>
      <ContextMenu
        onOpenChange={(open) => {
          setDesktopMenuOpen(open);
          if (open) {
            setMenuMode(resolveMenuMode());
          } else {
            setDesktopSub(null);
          }
        }}
      >
        <ContextMenuTrigger asChild>
          <div>{children({ highlighted: false })}</div>
        </ContextMenuTrigger>
        <ContextMenuContent
          className="w-[250px] rounded-xl border-[color:var(--surface-border)] bg-card p-[5px] text-[13px] shadow-[0_18px_50px_rgba(0,0,0,0.16)]"
          aria-label={menuMode === 'bulk' ? bulkTitle : labels.menuLabel}
        >
          {menuMode === 'bulk' ? (
            <>
              <ContextMenuLabel
                className={cn(
                  desktopItemClass,
                  'px-[9px] py-1.5 text-[11.5px] font-semibold text-muted-foreground',
                )}
              >
                {bulkTitle}
              </ContextMenuLabel>
              <ContextMenuSeparator className="mx-0 my-1 bg-[color:var(--surface-border)]" />
              {canMutateTargets ? (
                <>
                  <ContextMenuSub
                    open={desktopSub === 'status'}
                    onOpenChange={(open) =>
                      openDesktopSub(open ? 'status' : null)
                    }
                  >
                    <ContextMenuSubTrigger
                      className={cn(
                        desktopItemClass,
                        'focus:bg-[color:var(--surface-2)] data-[state=open]:bg-[color:var(--surface-2)]',
                      )}
                    >
                      <CircleDot
                        className="text-muted-foreground"
                        aria-hidden
                      />
                      <span className="min-w-0 flex-1">{labels.status}</span>
                    </ContextMenuSubTrigger>
                    <ContextMenuSubContent className="w-[190px] rounded-xl border-[color:var(--surface-border)] p-[5px] shadow-[0_18px_50px_rgba(0,0,0,0.16)]">
                      {statusItems(
                        (status) => void handleSetStatus(status),
                        true,
                      )}
                    </ContextMenuSubContent>
                  </ContextMenuSub>
                  <ContextMenuSub
                    open={desktopSub === 'assignee'}
                    onOpenChange={(open) =>
                      openDesktopSub(open ? 'assignee' : null)
                    }
                  >
                    <ContextMenuSubTrigger
                      className={cn(
                        desktopItemClass,
                        'focus:bg-[color:var(--surface-2)] data-[state=open]:bg-[color:var(--surface-2)]',
                      )}
                    >
                      <UserCheck
                        className="text-muted-foreground"
                        aria-hidden
                      />
                      <span className="min-w-0 flex-1">{labels.assign}</span>
                    </ContextMenuSubTrigger>
                    <ContextMenuSubContent className="w-[300px] rounded-xl border-[color:var(--surface-border)] p-0 shadow-[0_18px_50px_rgba(0,0,0,0.16)]">
                      {assigneePanel}
                    </ContextMenuSubContent>
                  </ContextMenuSub>
                  <ContextMenuSub
                    open={desktopSub === 'tags'}
                    onOpenChange={(open) =>
                      openDesktopSub(open ? 'tags' : null)
                    }
                  >
                    <ContextMenuSubTrigger
                      className={cn(
                        desktopItemClass,
                        'focus:bg-[color:var(--surface-2)] data-[state=open]:bg-[color:var(--surface-2)]',
                      )}
                    >
                      <Tag className="text-muted-foreground" aria-hidden />
                      <span className="min-w-0 flex-1">{labels.tags}</span>
                    </ContextMenuSubTrigger>
                    <ContextMenuSubContent className="w-72 rounded-xl border-[color:var(--surface-border)] p-2 shadow-[0_18px_50px_rgba(0,0,0,0.16)]">
                      {tagsPanel}
                    </ContextMenuSubContent>
                  </ContextMenuSub>
                  <ContextMenuSeparator className="mx-0 my-1 bg-[color:var(--surface-border)]" />
                </>
              ) : null}
              {canExport ? (
                <ContextMenuItem
                  disabled={busy}
                  className={cn(
                    desktopItemClass,
                    'focus:bg-[color:var(--surface-2)]',
                  )}
                  onSelect={() => openBulkExport()}
                >
                  <Download className="text-muted-foreground" aria-hidden />
                  <span className="min-w-0 flex-1">{labels.exportCsv}</span>
                </ContextMenuItem>
              ) : null}
              {canMutateTargets ? (
                <>
                  {showBulkArchive ? (
                    <ContextMenuItem
                      disabled={busy}
                      className={cn(
                        desktopItemClass,
                        'focus:bg-[color:var(--surface-2)]',
                      )}
                      onSelect={() => void handleSetArchived(true)}
                    >
                      <Archive className="text-muted-foreground" aria-hidden />
                      <span className="min-w-0 flex-1">{labels.archive}</span>
                    </ContextMenuItem>
                  ) : null}
                  {showBulkUnarchive ? (
                    <ContextMenuItem
                      disabled={busy}
                      className={cn(
                        desktopItemClass,
                        'focus:bg-[color:var(--surface-2)]',
                      )}
                      onSelect={() => void handleSetArchived(false)}
                    >
                      <ArchiveRestore
                        className="text-muted-foreground"
                        aria-hidden
                      />
                      <span className="min-w-0 flex-1">{labels.unarchive}</span>
                    </ContextMenuItem>
                  ) : null}
                  <ContextMenuSeparator className="mx-0 my-1 bg-[color:var(--surface-border)]" />
                  <ContextMenuItem
                    disabled={busy}
                    className={cn(
                      desktopItemClass,
                      'text-destructive focus:bg-destructive/10 focus:text-destructive',
                    )}
                    onSelect={requestDelete}
                  >
                    <Trash2 aria-hidden />
                    <span className="min-w-0 flex-1">
                      {labels.deleteN.replace(
                        '{count}',
                        String(mutableTargetIds.length),
                      )}
                    </span>
                  </ContextMenuItem>
                </>
              ) : null}
            </>
          ) : (
            <>
              <div className="mb-1 grid grid-cols-3 gap-1 pb-1">
                <button
                  type="button"
                  className={desktopActionTileClass}
                  disabled={!phoneHref}
                  onClick={handleCall}
                >
                  <Phone className="size-[15px]" aria-hidden />
                  {labels.call}
                </button>
                <button
                  type="button"
                  className={desktopActionTileClass}
                  disabled={!email}
                  onClick={handleEmail}
                >
                  <Mail className="size-[15px]" aria-hidden />
                  {labels.email}
                </button>
                <button
                  type="button"
                  className={desktopActionTileClass}
                  disabled={!copyPayloadForLead(lead)}
                  onClick={() => void handleCopy()}
                >
                  <Copy className="size-[15px]" aria-hidden />
                  {labels.copy}
                </button>
              </div>
              <div className="border-t border-[color:var(--surface-border)] py-1">
                <ContextMenuItem
                  className={cn(
                    desktopItemClass,
                    'focus:bg-[color:var(--surface-2)]',
                  )}
                  onSelect={() => onOpenLead(lead)}
                >
                  <SquareArrowOutUpRight
                    className="text-muted-foreground"
                    aria-hidden
                  />
                  <span className="min-w-0 flex-1">{labels.open}</span>
                  <ContextMenuShortcut className={desktopShortcutClass}>
                    {LEADS_CTX_SHORTCUT.open}
                  </ContextMenuShortcut>
                </ContextMenuItem>
                {canMutateThis ? (
                  <>
                    <ContextMenuSub
                      open={desktopSub === 'status'}
                      onOpenChange={(open) =>
                        openDesktopSub(open ? 'status' : null)
                      }
                    >
                      <ContextMenuSubTrigger
                        className={cn(
                          desktopItemClass,
                          'focus:bg-[color:var(--surface-2)] data-[state=open]:bg-[color:var(--surface-2)]',
                        )}
                      >
                        <CircleDot
                          className="text-muted-foreground"
                          aria-hidden
                        />
                        <span className="min-w-0 flex-1">{labels.status}</span>
                        <ContextMenuShortcut
                          className={cn(desktopShortcutClass, 'ml-0')}
                        >
                          {LEADS_CTX_SHORTCUT.status}
                        </ContextMenuShortcut>
                      </ContextMenuSubTrigger>
                      <ContextMenuSubContent className="w-[190px] rounded-xl border-[color:var(--surface-border)] p-[5px] shadow-[0_18px_50px_rgba(0,0,0,0.16)]">
                        {statusItems(
                          (status) => void handleSetStatus(status),
                          true,
                        )}
                      </ContextMenuSubContent>
                    </ContextMenuSub>
                    <ContextMenuSub
                      open={desktopSub === 'assignee'}
                      onOpenChange={(open) =>
                        openDesktopSub(open ? 'assignee' : null)
                      }
                    >
                      <ContextMenuSubTrigger
                        className={cn(
                          desktopItemClass,
                          'focus:bg-[color:var(--surface-2)] data-[state=open]:bg-[color:var(--surface-2)]',
                        )}
                      >
                        <UserCheck
                          className="text-muted-foreground"
                          aria-hidden
                        />
                        <span className="min-w-0 flex-1">
                          {labels.assignee}
                        </span>
                        <ContextMenuShortcut
                          className={cn(desktopShortcutClass, 'ml-0')}
                        >
                          {LEADS_CTX_SHORTCUT.assignee}
                        </ContextMenuShortcut>
                      </ContextMenuSubTrigger>
                      <ContextMenuSubContent className="w-[300px] rounded-xl border-[color:var(--surface-border)] p-0 shadow-[0_18px_50px_rgba(0,0,0,0.16)]">
                        {assigneePanel}
                      </ContextMenuSubContent>
                    </ContextMenuSub>
                    <ContextMenuSub
                      open={desktopSub === 'tags'}
                      onOpenChange={(open) =>
                        openDesktopSub(open ? 'tags' : null)
                      }
                    >
                      <ContextMenuSubTrigger
                        className={cn(
                          desktopItemClass,
                          'focus:bg-[color:var(--surface-2)] data-[state=open]:bg-[color:var(--surface-2)]',
                        )}
                      >
                        <Tag className="text-muted-foreground" aria-hidden />
                        <span className="min-w-0 flex-1">{labels.tags}</span>
                        <ContextMenuShortcut
                          className={cn(desktopShortcutClass, 'ml-0')}
                        >
                          {LEADS_CTX_SHORTCUT.tags}
                        </ContextMenuShortcut>
                      </ContextMenuSubTrigger>
                      <ContextMenuSubContent className="w-72 rounded-xl border-[color:var(--surface-border)] p-2 shadow-[0_18px_50px_rgba(0,0,0,0.16)]">
                        {tagsPanel}
                      </ContextMenuSubContent>
                    </ContextMenuSub>
                  </>
                ) : null}
              </div>
              <div className="border-t border-[color:var(--surface-border)] py-1">
                <ContextMenuItem
                  disabled={busy || !canSave}
                  className={cn(
                    desktopItemClass,
                    'focus:bg-[color:var(--surface-2)]',
                  )}
                  onSelect={() => {
                    void (isCapacitorNative()
                      ? handleSaveToContacts()
                      : handleDownloadVCard());
                  }}
                >
                  {isCapacitorNative() ? (
                    <Contact className="text-muted-foreground" aria-hidden />
                  ) : (
                    <Download className="text-muted-foreground" aria-hidden />
                  )}
                  <span className="min-w-0 flex-1">
                    {isCapacitorNative()
                      ? labels.saveToContacts
                      : labels.downloadVCard}
                  </span>
                </ContextMenuItem>
                {canMutateThis ? (
                  <ContextMenuItem
                    disabled={busy}
                    className={cn(
                      desktopItemClass,
                      'focus:bg-[color:var(--surface-2)]',
                    )}
                    onSelect={() => void handleSetArchived(!thisLeadArchived)}
                  >
                    {thisLeadArchived ? (
                      <ArchiveRestore
                        className="text-muted-foreground"
                        aria-hidden
                      />
                    ) : (
                      <Archive className="text-muted-foreground" aria-hidden />
                    )}
                    <span className="min-w-0 flex-1">
                      {thisLeadArchived ? labels.unarchive : labels.archive}
                    </span>
                    <ContextMenuShortcut className={desktopShortcutClass}>
                      {LEADS_CTX_SHORTCUT.archive}
                    </ContextMenuShortcut>
                  </ContextMenuItem>
                ) : null}
              </div>
              {canMutateThis ? (
                <div className="border-t border-[color:var(--surface-border)] pt-1">
                  <ContextMenuItem
                    disabled={busy}
                    className={cn(
                      desktopItemClass,
                      'text-destructive focus:bg-destructive/10 focus:text-destructive',
                    )}
                    onSelect={requestDelete}
                  >
                    <Trash2 aria-hidden />
                    <span className="min-w-0 flex-1">{labels.delete}</span>
                    <ContextMenuShortcut className={desktopShortcutClass}>
                      {LEADS_CTX_SHORTCUT.delete}
                    </ContextMenuShortcut>
                  </ContextMenuItem>
                </div>
              ) : null}
            </>
          )}
        </ContextMenuContent>
      </ContextMenu>
      {deleteDialog}
    </>
  );
}
